import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, LogIn } from 'lucide-react';
import apiService from '../services/api';
import { mapLoginError, formatLockoutMessage } from '../utils/loginError';
import { validatePassword, PASSWORD_POLICY_MESSAGE } from '../utils/passwordPolicy';

const Login: React.FC = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (error) setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.email || !formData.password) {
      setError('Please fill in all fields');
      return;
    }

    const policyViolation = validatePassword(formData.password);
    if (policyViolation) {
      setError(policyViolation);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      // Call backend admin login to capture Authorization header
      const data = await apiService.adminLogin({ email: formData.email, password: formData.password });
      // Persist minimal user info if available; fall back to email
      const user = data?.user || {
        first_name: data?.first_name || 'Admin',
        last_name: data?.last_name || 'User',
        email: data?.email || formData.email,
        mobile_number: data?.mobile_number || '',
        role: data?.role || 'admin'
      };
      localStorage.setItem('user', JSON.stringify(user));
      navigate('/');
    } catch (err: unknown) {
      const result = mapLoginError(err);
      setError(
        result.kind === 'locked' ? formatLockoutMessage(result.retryAfterSeconds) : result.message
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="max-w-md w-full space-y-8 p-8">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Swasth Bharat</h1>
          <p className="text-gray-600">Admin Portal</p>
        </div>

        <div className="card">
          <div className="card-header">
            <h2 className="card-title text-center">Sign In</h2>
          </div>
          <div className="card-content">
            {error && (
              <div className="error mb-4">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="form-group">
                <label className="form-label">Email Address</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="form-input"
                  placeholder="Enter your email"
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Password</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    className="form-input pr-10"
                    placeholder="Enter your password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">{PASSWORD_POLICY_MESSAGE}</p>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="btn btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <div className="spinner"></div>
                    Signing in...
                  </>
                ) : (
                  <>
                    <LogIn size={16} />
                    Sign In
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login; 